import * as React from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { MyTask } from "../myTasksSlice";

export type StatusKey = "overdue" | "soon" | "ok" | "none";

export type TaskUrgency = {
    key: StatusKey;
    label: string;
    labelId: string;
    labelVars?: Record<string, string | number>;
    color: "default" | "error" | "warning";
    dateLabel: string | null;
    daysRemaining: number | null;
    timestamp: number | null;
};

const MS_PER_DAY = 86_400_000;

function getCalendarDay(date: Date): number {
    return Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
    );
}

export const getStatus = (
    deadlineISO?: string | null,
    now: Date = new Date()
): TaskUrgency => {
    const deadline = deadlineISO ? new Date(deadlineISO) : null;

    if (!deadline || Number.isNaN(deadline.getTime())) {
        return {
            key: "none",
            label: "Bez terminu",
            labelId: "task-deadline-none",
            color: "default",
            dateLabel: null,
            daysRemaining: null,
            timestamp: null,
        };
    }

    const daysRemaining = Math.round(
        (getCalendarDay(deadline) - getCalendarDay(now)) / MS_PER_DAY
    );

    const common = {
        daysRemaining,
        timestamp: deadline.getTime(),
        dateLabel: deadline.toLocaleDateString("pl-PL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }),
    };

    if (daysRemaining < 0) {
        const days = Math.abs(daysRemaining);

        return {
            ...common,
            key: "overdue",
            label:
                days === 1
                    ? "1 dzień po terminie"
                    : `${days} dni po terminie`,
            labelId:
                days === 1
                    ? "task-deadline-overdue-one"
                    : "task-deadline-overdue-many",
            labelVars: { days },
            color: "error",
        };
    }

    if (daysRemaining <= 3) {
        return {
            ...common,
            key: "soon",
            label:
                daysRemaining === 0
                    ? "Termin dzisiaj"
                    : daysRemaining === 1
                        ? "Termin jutro"
                        : `Pozostało ${daysRemaining} dni`,
            labelId:
                daysRemaining === 0
                    ? "task-deadline-today"
                    : daysRemaining === 1
                        ? "task-deadline-tomorrow"
                        : "task-deadline-days-left",
            labelVars:
                daysRemaining > 1
                    ? { days: daysRemaining }
                    : undefined,
            color: "warning",
        };
    }

    return {
        ...common,
        key: "ok",
        label: `Pozostało ${daysRemaining} dni`,
        labelId: "task-deadline-days-left",
        labelVars: { days: daysRemaining },
        color: "default",
    };
};

// Kolory wykorzystywane przez kreskę i etykietę terminu.
export const urgencyAppearance = {
    overdue: {
        barColor: "error.main",
        light: {
            color: "#A12C26",     // tekst i ikona
            bgcolor: "#FFF0EE",    // tło etykiety
        },
        dark: {
            color: "#FFAAA3",
            bgcolor: "#492A2C",
        },
    },
    soon: {
        barColor: "warning.main",
        light: { color: "#885600", bgcolor: "#FFF5DF" },
        dark: { color: "#F2CE86", bgcolor: "#423822" },
    },
    ok: {
        barColor: "primary.main",
        light: { color: "#5A687B", bgcolor: "#F1F4F7" },
        dark: { color: "#B7C3D4", bgcolor: "#2A3646" },
    },
    none: {
        barColor: "transparent",
        light: { color: "#5A687B", bgcolor: "#F1F4F7" },
        dark: { color: "#B7C3D4", bgcolor: "#2A3646" },
    },
} as const;

export const iconForStatus = (
    key: StatusKey
): React.ReactElement => {
    switch (key) {
        case "overdue":
            return (
                <ErrorOutlineIcon fontSize="small" color="error" />
            );

        case "soon":
            return (
                <WarningAmberIcon fontSize="small" color="warning" />
            );

        case "ok":
            return (
                <ScheduleIcon fontSize="small" color="primary" />
            );

        default:
            return (
                <ScheduleIcon fontSize="small" color="disabled" />
            );
    }
};

const statusRank: Record<StatusKey, number> = {
    overdue: 0,
    soon: 1,
    ok: 2,
    none: 3,
};

export const sortByUrgency = (
    items: readonly MyTask[],
    now: Date = new Date()
): MyTask[] => {
    return items
        .map(task => ({
            task,
            status: getStatus(task.deadline, now),
        }))
        .sort((a, b) => {
            const rankDifference =
                statusRank[a.status.key] - statusRank[b.status.key];

            if (rankDifference !== 0)
                return rankDifference;

            // W tej samej grupie: najwcześniejszy termin.
            // Dla dwóch brakujących terminów zachowujemy kolejność.
            if (
                a.status.timestamp === null ||
                b.status.timestamp === null
            ) {
                return 0;
            }

            return a.status.timestamp - b.status.timestamp;
        })
        .map(item => item.task);
};